import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import CircleIcon from "@mui/icons-material/Circle";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import CookieIcon from "@mui/icons-material/Cookie";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel";
import InfoIcon from "@mui/icons-material/Info";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link"; // Added for Third Party section
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import ShareIcon from "@mui/icons-material/Share";
import SourceIcon from "@mui/icons-material/Source";
import StorageIcon from "@mui/icons-material/Storage";
import UpdateIcon from "@mui/icons-material/Update"; // Added for Changes section

import {
  Box,
  Container,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMetaTags } from "../../../hooks/useMetaTags";

// --- CUSTOM SCROLL SPY HOOK ---
const useScrollSpy = (ids: string[], offset = 150) => {
  const [activeId, setActiveId] = useState<string>(ids[0]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset;
      const currentSection = ids.reduce((acc, id) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          return id;
        }
        return acc;
      }, ids[0]);
      setActiveId(currentSection);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ids, offset]);
  return activeId;
};

// --- STYLED COMPONENTS ---
const SectionTitle = ({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon?: React.ElementType;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, mt: 1 }}>
    {Icon && <Icon sx={{ color: amazonColors.orange, fontSize: 28 }} />}
    <Typography
      variant="h5"
      sx={{
        fontWeight: 700,
        color: "text.primary",
        fontSize: { xs: "1.2rem", md: "1.4rem" },
      }}
    >
      {children}
    </Typography>
  </Box>
);

const SectionText = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Typography
    variant="body1"
    paragraph
    sx={{
      fontSize: { xs: "0.875rem", sm: "1rem" },
      lineHeight: 1.7,
      color: "text.secondary",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const BulletList = ({ items }: { items: string[] | any }) => {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  return (
    <List dense sx={{ pl: 1 }}>
      {items.map((item: string, index: number) => (
        <ListItem key={index} alignItems="flex-start" sx={{ px: 0 }}>
          <ListItemIcon sx={{ minWidth: 24, mt: 1 }}>
            <CircleIcon sx={{ fontSize: 6, color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText
            primary={<span dangerouslySetInnerHTML={{ __html: item }} />}
            primaryTypographyProps={{
              variant: "body1",
              color: "text.secondary",
              fontSize: "0.95rem",
              component: "span",
            }}
          />
        </ListItem>
      ))}
    </List>
  );
};

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // --- DATA DEFINITION ---
  const SECTIONS = useMemo(
    () => [
      {
        id: "who-we-are",
        title: t("privacyPolicy.sections.whoWeAre"),
        icon: AdminPanelSettingsIcon,
      },
      {
        id: "info-collect",
        title: t("privacyPolicy.sections.infoCollect"),
        icon: SourceIcon,
      },
      {
        id: "how-we-use",
        title: t("privacyPolicy.sections.howWeUse"),
        icon: InfoIcon,
      },
      {
        id: "legal-bases",
        title: t("privacyPolicy.sections.legalBases"),
        icon: GavelIcon,
      },
      {
        id: "cookies",
        title: t("privacyPolicy.sections.cookies"),
        icon: CookieIcon,
      },
      {
        id: "sharing",
        title: t("privacyPolicy.sections.sharing"),
        icon: ShareIcon,
      },
      {
        id: "international",
        title: t("privacyPolicy.sections.international"),
        icon: LanguageIcon,
      },
      {
        id: "retention",
        title: t("privacyPolicy.sections.retention"),
        icon: StorageIcon,
      },
      {
        id: "rights",
        title: t("privacyPolicy.sections.rights"),
        icon: PersonIcon,
      },
      {
        id: "children",
        title: t("privacyPolicy.sections.children"),
        icon: ChildCareIcon,
      },
      {
        id: "security",
        title: t("privacyPolicy.sections.security"),
        icon: LockIcon,
      },
      {
        id: "third-party",
        title: t("privacyPolicy.sections.thirdParty"),
        icon: LinkIcon,
      },
      {
        id: "changes",
        title: t("privacyPolicy.sections.changes"),
        icon: UpdateIcon,
      },
      {
        id: "contact",
        title: t("privacyPolicy.sections.contact"),
        icon: ContactSupportIcon,
      },
    ],
    [t]
  );

  const sectionIds = useMemo(() => SECTIONS.map((s) => s.id), [SECTIONS]);
  const activeSection = useScrollSpy(sectionIds);

  useMetaTags({
    title: t("privacyPolicy.meta.title"),
    description: t("privacyPolicy.meta.description"),
    keywords: t("privacyPolicy.meta.keywords"),
  });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <Box sx={{ minHeight: "50vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl" disableGutters sx={{ px: 2 }}>
        {/* --- MOBILE NAV --- */}
        {isMobile && (
          <Paper
            variant="outlined"
            sx={{ mb: 4, p: 2, bgcolor: "grey.50" }}
            className="notranslate"
            translate="no"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {t("privacyPolicy.onThisPage")}
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  sx={{
                    transform: mobileMenuOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: mobileMenuOpen ? "block" : "none", mt: 2 }}>
              {SECTIONS.map((section, idx) => (
                <Link
                  key={section.id}
                  component="button"
                  onClick={() => scrollToSection(section.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    py: 1,
                    textDecoration: "none",
                    color:
                      activeSection === section.id
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                    fontWeight: activeSection === section.id ? 700 : 400,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.9rem",
                  }}
                >
                  {idx + 1}. {section.title}
                </Link>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", gap: 6 }}>
          {/* --- DESKTOP SIDEBAR --- */}
          {isMdUp && (
            <Box
              sx={{ width: 260, flexShrink: 0 }}
              className="notranslate"
              translate="no"
            >
              <Box sx={{ position: "sticky", top: 140 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("privacyPolicy.onThisPage")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {SECTIONS.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <Box component="li" key={section.id} sx={{ mb: 0 }}>
                        <Link
                          onClick={() => scrollToSection(section.id)}
                          sx={{
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            py: 0.75,
                            pl: 2,
                            borderLeft: "3px solid",
                            borderColor: isActive
                              ? "warning.main"
                              : "transparent",
                            color: isActive ? "text.primary" : "text.secondary",
                            fontWeight: isActive ? 600 : 400,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              color: "warning.main",
                              borderColor: "warning.light",
                              bgcolor: "grey.50",
                            },
                          }}
                        >
                          {section.title}
                        </Link>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {/* --- MAIN CONTENT --- */}
          <Box
            sx={{ flex: 1, maxWidth: { md: "800px" } }}
            className="notranslate"
            translate="no"
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                mb: 2,
                letterSpacing: "-0.5px",
              }}
            >
              {t("privacyPolicy.title")}
            </Typography>

            <SectionText>
              <span
                dangerouslySetInnerHTML={{ __html: t("privacyPolicy.intro") }}
              />
            </SectionText>

            {/* 1. WHO WE ARE */}
            <Box id="who-we-are" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={AdminPanelSettingsIcon}>
                {t("privacyPolicy.sections.whoWeAre")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.whoWeAre.text")}</SectionText>
              <BulletList
                items={t("privacyPolicy.whoWeAre.list", {
                  returnObjects: true,
                })}
              />
            </Box>

            {/* 2. INFORMATION WE COLLECT */}
            <Box id="info-collect" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={SourceIcon}>
                {t("privacyPolicy.sections.infoCollect")}
              </SectionTitle>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: "grey.50" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      gutterBottom
                    >
                      {t("privacyPolicy.infoCollect.provided.title")}
                    </Typography>
                    <BulletList
                      items={t("privacyPolicy.infoCollect.provided.list", {
                        returnObjects: true,
                      })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      gutterBottom
                    >
                      {t("privacyPolicy.infoCollect.automatic.title")}
                    </Typography>
                    <BulletList
                      items={t("privacyPolicy.infoCollect.automatic.list", {
                        returnObjects: true,
                      })}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      gutterBottom
                    >
                      {t("privacyPolicy.infoCollect.thirdParty.title")}
                    </Typography>
                    <BulletList
                      items={t("privacyPolicy.infoCollect.thirdParty.list", {
                        returnObjects: true,
                      })}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* 3. HOW WE USE */}
            <Box id="how-we-use" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={InfoIcon}>
                {t("privacyPolicy.sections.howWeUse")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.howWeUse.intro")}</SectionText>
              <BulletList
                items={t("privacyPolicy.howWeUse.list", {
                  returnObjects: true,
                })}
              />
            </Box>

            {/* 4. LEGAL BASES */}
            <Box id="legal-bases" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={GavelIcon}>
                {t("privacyPolicy.sections.legalBases")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.legalBases.intro")}</SectionText>
              <BulletList
                items={t("privacyPolicy.legalBases.list", {
                  returnObjects: true,
                })}
              />
            </Box>

            {/* 5. COOKIES */}
            <Box id="cookies" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={CookieIcon}>
                {t("privacyPolicy.sections.cookies")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.cookies.text")}</SectionText>
            </Box>

            {/* 6. SHARING */}
            <Box id="sharing" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={ShareIcon}>
                {t("privacyPolicy.sections.sharing")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.sharing.intro")}</SectionText>
              <BulletList
                items={t("privacyPolicy.sharing.list", { returnObjects: true })}
              />

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "#E3F2FD",
                  borderLeft: "4px solid #2196F3",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.primary">
                  {t("privacyPolicy.sharing.noSaleNote")}
                </Typography>
              </Box>
            </Box>

            {/* 7. INTERNATIONAL */}
            <Box id="international" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={LanguageIcon}>
                {t("privacyPolicy.sections.international")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.international.text")}</SectionText>
            </Box>

            {/* 8. RETENTION */}
            <Box id="retention" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={StorageIcon}>
                {t("privacyPolicy.sections.retention")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.retention.intro")}</SectionText>
              <BulletList
                items={t("privacyPolicy.retention.list", {
                  returnObjects: true,
                })}
              />
              <SectionText sx={{ mt: 2 }}>
                {t("privacyPolicy.retention.note")}
              </SectionText>
            </Box>

            {/* 9. RIGHTS */}
            <Box id="rights" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={PersonIcon}>
                {t("privacyPolicy.sections.rights")}
              </SectionTitle>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    fontWeight={700}
                  >
                    {t("privacyPolicy.rights.us.title")}
                  </Typography>
                  <BulletList
                    items={t("privacyPolicy.rights.us.list", {
                      returnObjects: true,
                    })}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    fontWeight={700}
                  >
                    {t("privacyPolicy.rights.ca.title")}
                  </Typography>
                  <BulletList
                    items={t("privacyPolicy.rights.ca.list", {
                      returnObjects: true,
                    })}
                  />
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, color: "text.secondary" }}
                  >
                    {t("privacyPolicy.rights.emailNote")}{" "}
                    <strong>support@us2tjk.com</strong>
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    fontWeight={700}
                  >
                    {t("privacyPolicy.rights.tj.title")}
                  </Typography>
                  <BulletList
                    items={t("privacyPolicy.rights.tj.list", {
                      returnObjects: true,
                    })}
                  />
                </Paper>
              </Box>
            </Box>

            {/* 10. CHILDREN */}
            <Box id="children" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={ChildCareIcon}>
                {t("privacyPolicy.sections.children")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.children.text")}</SectionText>
            </Box>

            {/* 11. SECURITY */}
            <Box id="security" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={LockIcon}>
                {t("privacyPolicy.sections.security")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.security.text")}</SectionText>
            </Box>

            {/* 12. THIRD PARTY (NEW) */}
            <Box id="third-party" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={LinkIcon}>
                {t("privacyPolicy.sections.thirdParty")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.thirdParty.text")}</SectionText>
            </Box>

            {/* 13. CHANGES (NEW) */}
            <Box id="changes" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={UpdateIcon}>
                {t("privacyPolicy.sections.changes")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.changes.text")}</SectionText>
            </Box>

            {/* 14. CONTACT */}
            <Box id="contact" sx={{ scrollMarginTop: "140px", mb: 6 }}>
              <SectionTitle icon={ContactSupportIcon}>
                {t("privacyPolicy.sections.contact")}
              </SectionTitle>
              <SectionText>{t("privacyPolicy.contact.intro")}</SectionText>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: "grey.100",
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={700}>
                  Chai Services LLC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Platform: US2TJK
                </Typography>

                <Box
                  sx={{
                    display: "inline-block",
                    px: 3,
                    py: 1,
                    bgcolor: "white",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Link
                    href="mailto:support@us2tjk.com"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      color: "primary.main",
                    }}
                  >
                    support@us2tjk.com
                  </Link>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 3 }}
                >
                  {t("privacyPolicy.contact.verifyNote")}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
